﻿import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useForm, FormProvider, useWatch, useFieldArray } from "react-hook-form";
import {
  DocumentService,
  EntityType,
  PaymentService,
  PropertyService,
  UnitService,
  PaidFlowTypesEnum,
  PaymentDueAccountVM,
  OrganizationService,
} from "@propertelligent/client-api";
import apiHelper from "../../utils/apiHelper";
import { NotificationType, PubSub, TopicsEnum } from "../../utils/pubsub";
import DateHelper from "../../utils/DateHelper";
import { UserLoginData } from "../../components/common/charts/Enums";
import InputField from "../form-fields/InputField";
import DescriptionTextarea from "../form-fields/DescriptionTextarea";
import CurrencyInput from "../form-fields/CurrencyInput";
import FixedFooter from "../listings/add-listing-footer/fixed-footer";
import SelectField from "../form-fields/SelectField";
import LeftArrowTopHeader from "../form-fields/LeftArrowTopHeader";
import { Accordion, AccordionSection } from "../card/accordion";
import SimpleOutlineButton from "../buttons/Simple-Outline-button";
import DocumentUploader from "../CustomUploader/DocumentUploader";
import SvgRoundIconButton from "../buttons/Svg-Round-Icon-button";
import { DeleteRedIcon } from "../../assets/svgs/DeleteRedIcon";
import RightSideModal from "../Modal/right-side-modal";
import Card from "../card";
import DynamicTabs from "../tabs/DynamicTabs";
import DuesORTransaction from "./DuesORTransaction";
import "./payment.css";
import SimpleSpinner from "../../components/loader";
import EmptyState from "../emptystate";
import { GetImages } from "../../utils/GetImages";
import { utcDateFormat } from "../../utils/helper";
import PayableCard from "../listings/listing-cards/payables";
import SimpleButton from "../buttons/Simple-button";
import PaymentDashboard from "./PaymentDashboard";
import { useCurrencySymbol } from "../../hooks/useCurrencySymbol";
import { tr } from "date-fns/locale";
import { useCancelNavigation } from "../../utils/useCancelNavigation";
import {
  buildInvoiceItemsFullyQualifiedFilter,
  filterDuesByPaidFlow,
  filterPayableDues,
  getPaymentListingPath,
  isApiFailure,
  loadPayableHeader,
  resolveRefreshAfterDeleteOutcome,
  scopeInvoiceItemsForEdit,
} from "./paymentListHelpers";
import {
  fetchAllPaymentReferenceDocumentIds,
  fetchFirstPaymentReferenceDocument,
  uploadPaymentReferenceDocument,
} from "./paymentDocumentHelpers";
// TYPES & INTERFACES
// ============================================================================

interface PaymentItem {
  id?: number;
  PaymentType: string;
  Amount: string;
  DueDate: string;
  GracePeriod: string;
  Description: string;
}

interface FormValues {
  PayeeId?: string;
  AttachPaymentId?: string;
  PropertyId?: string;
  UnitId?: string;
  documentfile?: File | null;
  AccountNumber?: string;
  InvoiceNo?: string;
  payments: PaymentItem[];
}

interface AddPayeeFormValues {
  payeeName: string;
  AccountTypeId: string;
}

interface AccordionState {
  basicDetailsOpen: boolean;
  paymentInfoOpen: boolean;
}

interface ModalState {
  payee: boolean;
  viewAll: boolean;
}

interface ApiData {
  payeeAccounts: any[];
  properties: any[];
  units: any[];
  paymentTypes: any[];
  accountPayeeTypes: any[];
  dues: any[];
  transactions: any[];
  paymentDetails: PaymentDueAccountVM | null;
  paymentDueAccountDetail: PaymentDueAccountVM | null;
}

interface EditPayableProps {
  fetchData?: () => void;
  isEntityProp?: boolean;
  entityIdProp?: number;
  entityTypeIdProp?: EntityType;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const INITIAL_ACCORDION_STATE: AccordionState = {
  basicDetailsOpen: true,
  paymentInfoOpen: false,
};

const INITIAL_MODAL_STATE: ModalState = {
  payee: false,
  viewAll: false,
};

const INITIAL_API_DATA: ApiData = {
  payeeAccounts: [],
  properties: [],
  units: [],
  paymentTypes: [],
  accountPayeeTypes: [],
  dues: [],
  transactions: [],
  paymentDetails: null,
  paymentDueAccountDetail: null,
};

const ACCOUNT_OPTIONS = [
  { Id: "Organization", Name: "Organization" },
  { Id: "Property", Name: "Property" },
  { Id: "Unit", Name: "Unit" },
];

const DEFAULT_PAYMENT_ITEM: PaymentItem = {
  id: undefined,
  PaymentType: "",
  Amount: "",
  DueDate: "",
  GracePeriod: "",
  Description: "",
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const EditPayable = ({ fetchData, isEntityProp, entityIdProp, entityTypeIdProp }: EditPayableProps) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const payeeAccountId = searchParams.get('pai');
  const organizationId = Number(sessionStorage.getItem(UserLoginData.organization_id));
  const currencySymbol = useCurrencySymbol();

  const [resetCounter, setResetCounter] = useState(0);
  const [modals, setModals] = useState<ModalState>(INITIAL_MODAL_STATE);
  const [data, setData] = useState<ApiData>(INITIAL_API_DATA);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHeader, setIsLoadingHeader] = useState(true);
  const [isDuesLoading, setIsDuesLoading] = useState(false);
  const [isTransactionsLoading, setIsTransactionsLoading] = useState(false);
  const [accordionState, setAccordionState] = useState<AccordionState>(INITIAL_ACCORDION_STATE);
  const [selectedPayment, setSelectedPayment] = useState<PaymentDueAccountVM | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [originalPaymentIds, setOriginalPaymentIds] = useState<number[]>([]);
  const [originalDocumentId, setOriginalDocumentId] = useState<number | null>(null);
  const [loadedDocumentFile, setLoadedDocumentFile] = useState<File | null>(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);


  // ðŸ‘‡ LOGIC: Prefer Props, fallback to URL Params
  const isEntity = isEntityProp ?? (searchParams.get('isEntity') === 'true');
  const entityId = entityIdProp ?? (Number(searchParams.get('entityId')) || undefined);
  const entityTypeId = entityTypeIdProp ?? (Number(searchParams.get('entityTypeId')) || undefined);

  const listingPath = useMemo(
    () =>
      getPaymentListingPath({
        isEntity,
        entityId,
        entityTypeId,
        paidFlowTypeId: PaidFlowTypesEnum.Payable,
      }),
    [isEntity, entityId, entityTypeId],
  );

  // Form Setup
  const paymentMethods = useForm<FormValues>({
    defaultValues: {
      PayeeId: "",
      AttachPaymentId: "",
      PropertyId: "",
      UnitId: "",
      documentfile: null,
      AccountNumber: "",
      InvoiceNo: "",
      payments: [DEFAULT_PAYMENT_ITEM],
    },
    mode: "onBlur",
  });

  const payeeMethods = useForm<AddPayeeFormValues>({
    defaultValues: { payeeName: "", AccountTypeId: "" },
    mode: "onBlur",
  });

  const {
    handleSubmit: handlePaymentSubmit,
    control,
    setValue,
    reset,
    formState: { errors, isDirty, isValid }
  } = paymentMethods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "payments"
  });

  const PayeeId = useWatch({ control, name: "PayeeId" });
  const AttachPaymentId = useWatch({ control, name: "AttachPaymentId" });

  // ============================================================================
  // API CALLS
  // ============================================================================

  const fetchPayeeAccounts = useCallback(async () => {
    const res = await apiHelper(
      PaymentService.getApiPayeeAccountOrganizationlist,
      { showNotification: false },
      organizationId,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      `IsOrganizationAccount==true`
    );
    return res?.data?.success || [];
  }, [organizationId]);

  const fetchPayableHeader = useCallback(
    () => loadPayableHeader(isEntity, entityTypeId, entityId, payeeAccountId),
    [isEntity, entityTypeId, entityId, payeeAccountId],
  );

  const fetchPayeeDues = useCallback(async (payeeId: string): Promise<PaymentDueAccountVM[] | null> => {
    // No payeeId to query with is an unresolvable state, not "no dues remain" -- returning []
    // here would make refreshAfterDelete think the account is empty and navigate the user away
    // even though the header it just fetched is still valid.
    if (!payeeId) return null;
    setIsDuesLoading(true);
    try {
      let res;

      // Conditional Logic for Dues List API
      if (isEntity && entityTypeId === EntityType.Property && entityId) {
        res = await apiHelper(
          PaymentService.getApiInvoicePayeeAccountPropertyList,
          { showNotification: false },
          Number(payeeId),
          Number(entityId)
        );
      }
      else if (isEntity && entityTypeId === EntityType.Unit && entityId) {
        res = await apiHelper(
          PaymentService.getApiInvoicePayeeAccountUnitList,
          { showNotification: false },
          Number(payeeId),
          Number(entityId)
        );
      }
      else if (isEntity && entityTypeId === EntityType.Tenant && entityId) {
        res = await apiHelper(
          PaymentService.getApiInvoicePayeeAccountTenantList,
          { showNotification: false },
          Number(entityId)
        );
      }
      else {
        // Fallback: Default Invoice List API
        res = await apiHelper(
          PaymentService.getApiPaymentDueAccountInvoicelist,
          { showNotification: false },
          payeeId
        );
      }

      if (isApiFailure(res)) {
        // A failed fetch is not the same as a genuinely empty list -- returning [] here would
        // make refreshAfterDelete think there are no dues left and eject the user from the page.
        PubSub.publish(TopicsEnum.Notification.toString(), {
          message: "Failed to fetch dues",
          type: NotificationType.Failure,
        });
        return null;
      }

      const dues = filterPayableDues<PaymentDueAccountVM>(res.data.success || []);
      setData(prev => ({ ...prev, dues }));

      if (isInitialLoad && dues.length > 0) {
        handlePaymentSelect(dues[0]);
        setIsInitialLoad(false);
      }

      return dues;
    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to fetch dues",
        type: NotificationType.Failure,
      });
      return null;
    } finally {
      setIsDuesLoading(false);
    }
  }, [isInitialLoad, isEntity, entityTypeId, entityId]);

  const fetchTransactions = useCallback(async () => {
    if (!PayeeId) return;
    setIsTransactionsLoading(true);
    try {
      const res = await apiHelper(
        OrganizationService.getApiOrganizationTransactionOrganizationlist,
        { showNotification: false },
        organizationId,
        5,
        1,
        undefined,
        undefined,
        undefined,
        `PayeeAccountId = ${PayeeId}`,
        "PaymentDate desc"
      );
      setData(prev => ({ ...prev, transactions: res?.data?.success || [] }));
    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to fetch transactions",
        type: NotificationType.Failure,
      });
    } finally {
      setIsTransactionsLoading(false);
    }
  }, [PayeeId, organizationId]);

  const fetchPaymentTypes = useCallback(async (payeeAccountTypeId: number) => {
    try {
      const res = await apiHelper(
        PaymentService.getApiPayeeAccountPaymentTypes,
        { showNotification: false },
        payeeAccountTypeId
      );
      setData(prev => ({ ...prev, paymentTypes: res?.data?.success || [] }));
    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to fetch Payment types",
        type: NotificationType.Failure,
      });
    }
  }, []);

  const loadInvoiceItems = useCallback(async (
    referenceNumber: string,
    payeeAccountId: string,
    paymentId?: number,
    paymentDueAccountIds?: number[] | null,
  ) => {
    try {
      const payeeIdNum = Number(payeeAccountId);
      const invoiceItemsResponse = await apiHelper(
        PaymentService.getApiPaymentDueAccountInvoiceitems,
        { showNotification: false },
        referenceNumber,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        buildInvoiceItemsFullyQualifiedFilter({
          paidFlowTypeId: PaidFlowTypesEnum.Payable,
          payeeAccountId: payeeIdNum,
        }),
      );

      const invoiceItems = scopeInvoiceItemsForEdit(
        filterDuesByPaidFlow(
          invoiceItemsResponse?.data?.success || [],
          PaidFlowTypesEnum.Payable,
          { keepUnknown: true },
        ),
        {
          paymentDueAccountIds,
          payeeAccountId: payeeIdNum,
        },
      );
      if (invoiceItems.length === 0) return null;

      const firstItem = invoiceItems[0];
      const loadedDoc = await fetchFirstPaymentReferenceDocument(
        invoiceItems.map((item: any) => item.id),
        paymentId,
      );
      if (loadedDoc) {
        setOriginalDocumentId(loadedDoc.documentId);
      } else {
        setOriginalDocumentId(null);
      }
      setLoadedDocumentFile(loadedDoc?.file ?? null);

      const formData: FormValues = {
        PayeeId: String(payeeAccountId),
        AttachPaymentId: firstItem.formatted?.EntityTypeId,
        AccountNumber: firstItem.accountNumber,
        InvoiceNo: firstItem.referenceNumber,
        documentfile: loadedDoc?.file ?? null,
        payments: invoiceItems.map((item: any) => ({
          id: item.id,
          PaymentType: String(item.paymentTypeId),
          Amount: String(item.amount),
          DueDate: DateHelper.convertUTCtoDateTime2(item.dueDate),
          GracePeriod: String(item.gracePeriodInDays),
          Description: item.description,
        }))
      };

      if (firstItem.formatted?.EntityTypeId === "Unit") {
        formData.UnitId = String(firstItem.entityId);
      } else if (firstItem.formatted?.EntityTypeId === "Property") {
        formData.PropertyId = String(firstItem.entityId);
      }

      return formData;
    } catch (error) {
      console.error("Failed to load invoice items:", error);
      return null;
    }
  }, []);

  const fetchInitialData = useCallback(async () => {
    try {
      setIsLoading(true);

      const [
        payeeAccountsRes,
        propertiesRes,
        unitsRes,
        accountPayeeTypesRes,
        commonPaymentData,
      ] = await Promise.all([
        fetchPayeeAccounts(),
        apiHelper(PropertyService.getApiPropertyOrganizationlookup, { showNotification: false }, organizationId),
        apiHelper(UnitService.getApiUnitOrganizationlookup, { showNotification: false }, organizationId),
        apiHelper(PaymentService.getApiPayeeAccountPayeeTypes, { showNotification: false }),
        fetchPayableHeader(),
      ]);

      const newData: ApiData = {
        payeeAccounts: payeeAccountsRes || [],
        properties: propertiesRes?.data?.success || [],
        units: unitsRes?.data?.success || [],
        accountPayeeTypes: accountPayeeTypesRes?.data?.success || [],
        paymentDetails: commonPaymentData,
        paymentDueAccountDetail: commonPaymentData,
        paymentTypes: [],
        dues: [],
        transactions: []
      };

      setData(newData);

      // Populate Form Logic
      if (commonPaymentData) {
        const { payeeAccountId: detailPayeeId, entityId: resEntityId, accountNumber, referenceNumber, formatted } = commonPaymentData;

        const finalPayeeId = detailPayeeId || payeeAccountId;

        const baseForm: FormValues = {
          PayeeId: String(finalPayeeId),
          AttachPaymentId: formatted?.EntityTypeId,
          PropertyId: "",
          UnitId: "",
          documentfile: null,
          AccountNumber: accountNumber,
          InvoiceNo: referenceNumber,
          payments: []
        };

        if (formatted?.EntityTypeId === "Property") {
          baseForm.PropertyId = String(resEntityId);
        } else if (formatted?.EntityTypeId === "Unit") {
          baseForm.UnitId = String(resEntityId);
        }

        if (referenceNumber && Number(finalPayeeId) > 0) {

          let duesResponse;

          if (isEntity && entityTypeId === EntityType.Property && entityId) {
            duesResponse = await apiHelper(
              PaymentService.getApiInvoicePayeeAccountPropertyList,
              { showNotification: false },
              Number(finalPayeeId),
              Number(entityId)
            );
          }
          else if (isEntity && entityTypeId === EntityType.Unit && entityId) {
            duesResponse = await apiHelper(
              PaymentService.getApiInvoicePayeeAccountUnitList,
              { showNotification: false },
              Number(finalPayeeId),
              Number(entityId)
            );
          }
          else if (isEntity && entityTypeId === EntityType.Tenant && entityId) {
            duesResponse = await apiHelper(
              PaymentService.getApiInvoicePayeeAccountTenantList,
              { showNotification: false },
              Number(entityId)
            );
          }
          else {
            duesResponse = await apiHelper(
              PaymentService.getApiPaymentDueAccountInvoicelist,
              { showNotification: false },
              finalPayeeId
            );
          }

          const dues = filterPayableDues<PaymentDueAccountVM>(duesResponse?.data?.success || []);
          setData(prev => ({ ...prev, dues }));

          const defaultSelected = dues.find((d) => d.referenceNumber === referenceNumber);
          let invoiceData;

          if (defaultSelected) {
            setSelectedPayment(defaultSelected);
            // Pass defaultSelected.id to fetch the document
            invoiceData = await loadInvoiceItems(
              referenceNumber,
              String(finalPayeeId),
              defaultSelected.id,
              defaultSelected.paymentDueAccountIds,
            );
          } else {
            invoiceData = await loadInvoiceItems(referenceNumber, String(finalPayeeId));
          }

          if (invoiceData) {
            const loadedIds = invoiceData.payments
              .map(p => p.id)
              .filter((id): id is number => !!id && id > 0);
            setOriginalPaymentIds(loadedIds);
            reset(invoiceData);
            return;
          }
        }

        reset(baseForm);
      }
    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to load required data",
        type: NotificationType.Failure,
      });
    } finally {
      setIsLoading(false);
      setIsLoadingHeader(false);
    }
  }, [
    organizationId,
    payeeAccountId,
    isEntity,
    entityTypeId,
    entityId,
    reset,
    fetchPayeeAccounts,
    loadInvoiceItems,
    fetchPayableHeader,
  ]);

  // ============================================================================
  // HANDLERS & EFFECTS
  // ============================================================================

  const toggleModal = useCallback((modalName: keyof ModalState) => {
    setModals(prev => ({ ...prev, [modalName]: !prev[modalName] }));
  }, []);

  const toggleAccordion = useCallback((section: keyof AccordionState) => {
    setAccordionState(prev => {
      if (prev[section]) return { ...prev, [section]: false };
      return {
        basicDetailsOpen: section === 'basicDetailsOpen',
        paymentInfoOpen: section === 'paymentInfoOpen',
      };
    });
  }, []);

  const handlePaymentSelect = useCallback(async (payment: PaymentDueAccountVM) => {
    setSelectedPayment(payment);
    if (!payment.referenceNumber) return;

    try {
      // Panel-level loader: the page-level isLoading replaced the whole screen, list included.
      setIsDetailLoading(true);
      // Pass payment.id here to fetch the document
      const formData = await loadInvoiceItems(
        payment.referenceNumber,
        String(payment.payeeAccountId),
        payment.id,
        payment.paymentDueAccountIds,
      );
      if (formData) {
        const loadedIds = formData.payments
          .map(p => p.id)
          .filter((id): id is number => !!id && id > 0);
        setOriginalPaymentIds(loadedIds);
        reset(formData);
        setAccordionState({ basicDetailsOpen: true, paymentInfoOpen: true });
      }
    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to load payment details",
        type: NotificationType.Failure,
      });
    } finally {
      setIsDetailLoading(false);
    }
  }, [reset, loadInvoiceItems]);

  /** After header Delete: reload dues in place, or leave Edit when nothing remains. */
  const refreshAfterDelete = useCallback(async () => {
    setIsLoadingHeader(true);
    try {
      const header = await fetchPayableHeader();
      if (!header) {
        setData((prev) => ({
          ...prev,
          paymentDueAccountDetail: null,
          paymentDetails: null,
          dues: [],
        }));
        setSelectedPayment(null);
        navigate(listingPath, { replace: true });
        return;
      }

      setData((prev) => ({
        ...prev,
        paymentDueAccountDetail: header,
        paymentDetails: header,
      }));

      const payeeId = String(header.payeeAccountId || payeeAccountId || "");
      const dues = await fetchPayeeDues(payeeId);
      if (dues === null) {
        // Dues fetch failed -- keep the user on the current header/selection rather than
        // treating an unknown result as "no dues remain" and ejecting them from the page.
        return;
      }
      const outcome = resolveRefreshAfterDeleteOutcome(dues, selectedPayment);

      if (outcome.action === "navigate") {
        navigate(listingPath, { replace: true });
        return;
      }

      await handlePaymentSelect(outcome.payment);
    } catch {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to refresh payments after delete",
        type: NotificationType.Failure,
      });
    } finally {
      setIsLoadingHeader(false);
    }
  }, [
    fetchPayableHeader,
    fetchPayeeDues,
    payeeAccountId,
    selectedPayment,
    handlePaymentSelect,
    navigate,
    listingPath,
  ]);

  const openPaymentInfo = useCallback(() => {
    setAccordionState({ basicDetailsOpen: false, paymentInfoOpen: true });
  }, []);

  const getEntityInfo = useCallback((formData: FormValues) => {
    if (formData.PropertyId && Number(formData.PropertyId) > 0) {
      return { entityId: Number(formData.PropertyId), entityTypeId: EntityType.Property };
    } else if (formData.UnitId && Number(formData.UnitId) > 0) {
      return { entityId: Number(formData.UnitId), entityTypeId: EntityType.Unit };
    }
    return { entityId: organizationId, entityTypeId: EntityType.Organization };
  }, [organizationId]);

  const createPaymentObject = useCallback((
    formData: FormValues,
    item: PaymentItem,
    entityId: number,
    entityTypeId: EntityType
  ) => ({
    amount: Number(item.Amount),
    description: item.Description,
    dueDate: utcDateFormat(item.DueDate),
    entityId,
    entityTypeId,
    accountNumber: formData.AccountNumber,
    payeeAccountId: Number(formData.PayeeId) || 0,
    gracePeriodInDays: Number(item.GracePeriod) || 0,
    referenceNumber: formData.InvoiceNo,
    paymentTypeId: Number(item.PaymentType) || 0,
    paidFlowTypeId: PaidFlowTypesEnum.Payable,
    organizationId
  }), [organizationId]);

  const uploadDocument = useCallback(async (paymentId: number, file: File) => {
    await uploadPaymentReferenceDocument(paymentId, file);
  }, []);

  const handlePaymentUpdate = useCallback(async (formData: FormValues) => {
    try {
      const { entityId, entityTypeId } = getEntityInfo(formData);

      const currentIds = new Set(
        formData.payments
          .map(p => p.id)
          .filter((id): id is number => !!id && id > 0)
      );
      const idsToDelete = originalPaymentIds.filter(id => !currentIds.has(id));

      const documentUnchanged =
        !!formData.documentfile && formData.documentfile === loadedDocumentFile;
      // Collected before any upload so the new copies are never in this list.
      const previousDocumentIds =
        originalDocumentId && !documentUnchanged
          ? await fetchAllPaymentReferenceDocumentIds([...originalPaymentIds, selectedPayment?.id])
          : [];

      const deletePromises = idsToDelete.map(id =>
        apiHelper(PaymentService.deleteApiPaymentDueAccount, { showNotification: false }, id)
      );

      const updateCreatePromises = formData.payments.map(async (item) => {
        const paymentObj = createPaymentObject(formData, item, entityId, entityTypeId);
        const isExisting = item.id && item.id > 0;

        const apiMethod = isExisting
          ? PaymentService.putApiPaymentDueAccount
          : PaymentService.postApiPaymentDueAccount;

        const payload = isExisting ? { ...paymentObj, id: item.id } : paymentObj;

        const res = await apiHelper(apiMethod, { showNotification: false }, payload);

        // Existing lines already hold the unchanged PDF; re-uploading only stacked duplicates.
        if (res?.data?.success && formData.documentfile && !(documentUnchanged && isExisting)) {
          await uploadDocument(res.data.success.id, formData.documentfile);
        }
      });

      await Promise.all([...deletePromises, ...updateCreatePromises]);

      // Removed or replaced PDF: delete every stored copy once, not just the one displayed.
      if (previousDocumentIds.length > 0) {
        try {
          await Promise.all(
            previousDocumentIds.map(docId =>
              apiHelper(DocumentService.deleteApiDocument, { showNotification: false }, docId)
            )
          );
        } catch (deleteErr) {
          console.warn("Failed to delete old document:", deleteErr);
        }
      }

      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Payments updated successfully",
        type: NotificationType.Success,
        title: "Payment Updated",
        icon: <img src={GetImages.toastPaymentAccountIcon} alt="success" width="20" height="20" />,
      });

      reset({
        PayeeId: "",
        AttachPaymentId: "",
        PropertyId: "",
        UnitId: "",
        documentfile: null,
        AccountNumber: "",
        InvoiceNo: "",
        payments: [DEFAULT_PAYMENT_ITEM]
      });

      payeeMethods.reset();
      setAccordionState(INITIAL_ACCORDION_STATE);
      setResetCounter(prev => prev + 1);
      setOriginalPaymentIds([]);
      setOriginalDocumentId(null);
      fetchData?.();
      navigate(listingPath);
    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to update payment",
        type: NotificationType.Failure,
      });
    }
  }, [
    originalPaymentIds, navigate, organizationId, reset, payeeMethods,
    fetchData, getEntityInfo, createPaymentObject, uploadDocument,
    originalDocumentId, loadedDocumentFile, selectedPayment
  ]);

  const onAddPayeeSubmit = useCallback(async (formData: AddPayeeFormValues) => {
    setIsLoading(true);
    try {
      const response = await apiHelper(
        PaymentService.postApiPayeeAccount,
        { showNotification: true, successMessage: "Payee Account Added Successfully" },
        {
          accountName: formData.payeeName,
          organizationId,
          payeeAccountTypeId: Number(formData.AccountTypeId),
        }
      );

      if (response?.data?.success) {
        const payeeAccounts = await fetchPayeeAccounts();
        setData(prev => ({ ...prev, payeeAccounts }));
        setValue("PayeeId", String(response.data.success.id), { shouldValidate: true });
        toggleModal('payee');
        payeeMethods.reset();
      }
    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Unable to Add Payee Account",
        type: NotificationType.Failure,
      });
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, toggleModal, payeeMethods, setValue, fetchPayeeAccounts]);

  const onCancel = useCallback(() => navigate(listingPath), [navigate, listingPath]);
  const addPaymentRow = useCallback(() => append({ ...DEFAULT_PAYMENT_ITEM, id: undefined }), [append]);
  const removePaymentRow = useCallback((index: number) => remove(index), [remove]);

  const handleTabChange = useCallback((tabId: string) => {
    if (tabId === "dues") fetchPayeeDues(PayeeId);
    else if (tabId === "transactions") fetchTransactions();
  }, [fetchPayeeDues, fetchTransactions, PayeeId]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    const payeeAccountTypeId = data.payeeAccounts.find(x => x.id === Number(PayeeId))?.payeeAccountTypeId;
    if (payeeAccountTypeId) fetchPaymentTypes(payeeAccountTypeId);
  }, [PayeeId, data.payeeAccounts, fetchPaymentTypes]);


  const onNavigate = useCancelNavigation(listingPath);

  // ============================================================================
  // MEMOIZED VALUES
  // ============================================================================

  const payeeAccountsOptions = useMemo(() => data.payeeAccounts.map(t => ({ Id: t.id, Name: t.accountName })), [data.payeeAccounts]);
  const propertyOptions = useMemo(() => data.properties.map(t => ({ Id: t.id, Name: t.name })), [data.properties]);
  const unitOptions = useMemo(() => data.units.map(t => ({ Id: t.id, Name: t.name })), [data.units]);
  const paymentTypeOptions = useMemo(() => data.paymentTypes.map(t => ({ Id: String(t.id), Name: t.name })), [data.paymentTypes]);
  const accountPayeeTypesOptions = useMemo(() => data.accountPayeeTypes.map(t => ({ Id: t.id, Name: t.name })), [data.accountPayeeTypes]);

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const renderBasicDetails = useCallback(() => (
    <Accordion>
      <AccordionSection title="Basic Details" isOpen={accordionState.basicDetailsOpen} onToggle={() => toggleAccordion('basicDetailsOpen')}>
        <div className="accordion-body">
          <div className="row">
            <div className="col-md-12">
              <form>
                <div className="row align-items-center">
                  <div className="col-md-6 d-none">
                    <SelectField
                      placeholderText="Select Payee"
                      name="PayeeId"
                      label="Select Payee"
                      required
                      requiredMessage="Select Payee is required"
                      options={payeeAccountsOptions}
                      disabled={isLoading}
                    />
                  </div>
                  {Number(PayeeId) > 0 && (
                    <div className="col-md-6 d-none">
                      <label className="fw-600 mt-md-4 w-100 mt-lg-4 mt-0 d-block"> </label>
                      <SimpleOutlineButton
                        style={{ paddingTop: 9, paddingBottom: 9 }}
                        className="mt-md-2 w-100 mt-lg-2 mt-0"
                        type="button"
                        label="View All Dues & Transactions"
                        onClick={() => toggleModal('viewAll')}
                      />
                    </div>
                  )}
                  <p className="select_payee_text my-3 d-none">
                    Can't see the payee? to create new,
                    <span onClick={() => toggleModal('payee')} className="select_payee_text_Primary">Click Here</span>
                  </p>
                  <div className="col-md-4">
                    <SelectField
                      placeholderText="Select an Option"
                      name="AttachPaymentId"
                      label="Attach Payment To"
                      required
                      requiredMessage="Attach Payment To is required"
                      options={ACCOUNT_OPTIONS}
                      disabled={isLoading}
                    />
                  </div>
                  {AttachPaymentId === "Property" && (
                    <div className="col-md-4">
                      <SelectField
                        placeholderText="Select Property"
                        name="PropertyId"
                        label="Property"
                        required
                        requiredMessage="Property is required"
                        options={propertyOptions}
                        disabled={isLoading}
                        isSearchable={true}
                      />
                    </div>
                  )}
                  {AttachPaymentId === "Unit" && (
                    <div className="col-md-4">
                      <SelectField
                        placeholderText="Select Unit"
                        name="UnitId"
                        label="Unit"
                        required
                        requiredMessage="Unit is required"
                        options={unitOptions}
                        disabled={isLoading}
                        isSearchable={true}
                      />
                    </div>
                  )}
                </div>
                <div className="row mt-2">
                  <div className="col-md-4">
                    <InputField
                      name="AccountNumber"
                      label="Account Number"
                      required
                      type="text"
                      placeholder="Enter Account Number"
                      requiredMessage="Account Number is required"
                      validation={{
                        maxLength: {
                          value: 50,
                          message: "Account number cannot exceed 50 characters"
                        }
                      }}
                    />
                  </div>
                  <div className="col-md-4">
                    <InputField
                      name="InvoiceNo"
                      label="Invoice No./Bill No."
                      type="text"
                      placeholder="Enter Invoice No./Bill No."
                      required
                      requiredMessage="Invoice No./Bill No. is required"
                      validation={{
                        required: "Invoice No./Bill No. is required",
                        maxLength: {
                          value: 100,
                          message: "Invoice number cannot exceed 100 characters"
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="row mt-2">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="fw-600">
                        Upload Bill / Invoice <span className="optionalLabel">(optional)</span>
                      </label>
                      <DocumentUploader name="documentfile" maxSizeMB={5} resetTrigger={resetCounter} />
                    </div>
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-12">
                    <SimpleButton type="button" label="Next" onClick={openPaymentInfo} />
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </AccordionSection>
    </Accordion>
  ), [
    accordionState.basicDetailsOpen, PayeeId, AttachPaymentId,
    payeeAccountsOptions, propertyOptions, unitOptions, isLoading,
    toggleAccordion, toggleModal, openPaymentInfo
  ]);

  const renderPaymentInfo = useCallback(() => (
    <Accordion>
      <AccordionSection title="Payment Information" isOpen={accordionState.paymentInfoOpen} onToggle={() => toggleAccordion('paymentInfoOpen')}>


        <div className="accordion-body">
          {fields.map((field, index) => (
            <div className="payment-row" key={field.id}>
              <div className="payment-fields-group">
                <div className="payment-field">
                  <SelectField
                    placeholderText="Select payment type"
                    name={`payments.${index}.PaymentType`}
                    label={index === 0 ? "Payment Type" : ""}
                    required={true}
                    requiredMessage="Payment Type is required"
                    options={paymentTypeOptions}
                    isAsterisk={index === 0}
                  />
                </div>
                <div className="payment-field">
                  <CurrencyInput
                    name={`payments.${index}.Amount`}
                    label={index === 0 ? `Amount (${currencySymbol})` : ""}
                    required={true}
                    requiredMessage="Amount is required"
                    placeholder="Enter Amount"
                    disabled={isLoading}
                  />
                </div>
                <div className="payment-field">
                  <InputField
                    name={`payments.${index}.DueDate`}
                    label={index === 0 ? "Due Date" : ""}
                    type="date"
                    required={true}
                    requiredMessage="Due Date is required"
                    isAsterisk={index === 0}
                  />
                </div>
                <div className="payment-field">
                  <InputField
                    name={`payments.${index}.GracePeriod`}
                    label={index === 0 ? "Grace Period (Days)" : ""}
                    required={true}
                    requiredMessage="Grace Period is required"
                    isAsterisk={index === 0}
                    type="number"
                    placeholder="Enter Grace Period"
                    validation={{
                      min: { value: 0, message: "Grace period cannot be negative" },
                      maxLength: {
                        value: 2,
                        message: "Grace period cannot exceed 99 days"
                      }
                    }}
                  />
                </div>
                <div className="payment-field description-field">
                  <DescriptionTextarea
                    name={`payments.${index}.Description`}
                    label={index === 0 ? "Description" : ""}
                    placeholder="Enter description"
                    requiredMessage="Description is required"
                    required={true}
                    rows={1}
                    alignCounterWithError
                    validation={{
                      maxLength: {
                        value: 250,
                        message: "Description cannot exceed 250 characters"
                      }
                    }}
                  />
                </div>
              </div>
              <div className="payment-action">
                {/* Matches the real fields' label height -- but ONLY on the row that
                    actually has labels (index 0), since .payment-action has no label
                    of its own and otherwise aligns to the top of the row (the label
                    line) instead of the input line beneath it. */}
                {index === 0 && <div style={{ height: '21px', marginBottom: '0.5rem' }}></div>}

                {fields.length > 1 && (
                  <SvgRoundIconButton
                    style={{ width: 40, background: "#FDE8E8" }}
                    Icon={<DeleteRedIcon fill="#E02424" width="22" height="22" />}
                    onClick={() => removePaymentRow(index)}
                  />
                )}
              </div>
            </div>
          ))}

          <div className="d-flex align-items-center gap-2 mt-3">
            <SimpleOutlineButton
              type="button"
              label="Add More"
              onClick={addPaymentRow}
              isDisabled={fields.length >= 5}
            />
            {fields.length >= 5 && (
              <p className="text-danger f-12 mb-0">
                Maximum 5 payments allowed
              </p>
            )}
          </div>
        </div>
      </AccordionSection>
    </Accordion>
  ), [
    accordionState.paymentInfoOpen, fields, paymentTypeOptions,
    resetCounter, toggleAccordion, addPaymentRow, removePaymentRow
  ]);

  const renderDuesContent = useCallback(() => {
    if (isDuesLoading) return <div className='loading-card'><SimpleSpinner myClassName='Icon40 mt-5' /></div>;
    if (data.dues.length === 0) return <EmptyState heading="No Due Payments" icon={GetImages.DuePayments} isButton={false} isBorder={false} preserveIconColors />;
    return data.dues.map(item => <DuesORTransaction key={item.id} item={item} />);
  }, [data.dues, isDuesLoading]);

  const renderTransactionsContent = useCallback(() => {
    if (isTransactionsLoading) return <div className='loading-card'><SimpleSpinner myClassName='Icon40 mt-5' /></div>;
    if (data.transactions.length === 0) return <EmptyState heading="No Transactions" icon={GetImages.DuePayments} isButton={false} isBorder={false} preserveIconColors />;
    return data.transactions.map(item => <DuesORTransaction key={item.id} item={item} />);
  }, [data.transactions, isTransactionsLoading]);

  if (isLoading) {
    return (
      <SimpleSpinner myClassName={"Icon50"} isFullPage={true} />
    );
  }


  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <>
      <div className="d-flex flex-column gap-2 full-page-content edit-payment-container">
        <PayableCard
          id="PayableDetailCard"
          className="payable-detail-header"
          dataHookState={{
            rows: data.paymentDueAccountDetail ? [data.paymentDueAccountDetail] : [],
            loading: isLoadingHeader,
          }}
          isFooter={true}
          isEntity={isEntity}
          entityId={entityId}
          entityTypeId={entityTypeId}
          onRefresh={refreshAfterDelete}
        />
        {data.paymentDueAccountDetail && (
          <>
            <LeftArrowTopHeader title="Edit Payable" backPath={listingPath} />



            <Card className="no-bg p-0 mt-0">
              <div className="edit-payment-content">
                <div className="row align-items-center">
                  <div className="col-md-4">
                    <PaymentDashboard
                      payments={data.dues}
                      selectedPayment={selectedPayment}
                      onPaymentSelect={handlePaymentSelect}
                    />
                  </div>
                  <div className="col-md-8">

                    <FormProvider {...paymentMethods}>
                      <div className="d-flex flex-column gap-3">
                        {isLoading || isDetailLoading ? (
                          <SimpleSpinner myClassName="Icon40 mt-5" />
                        ) : (
                          <>
                            {renderBasicDetails()}
                            {renderPaymentInfo()}
                          </>
                        )}
                      </div>
                    </FormProvider>
                  </div>
                </div>
              </div>
            </Card>

            <FixedFooter
              onCancel={onNavigate}
              onSubmit={handlePaymentSubmit(handlePaymentUpdate)}
              loading={paymentMethods.formState.isSubmitting}
              buttonLabel="Save"
              isSubmitDisabled={paymentMethods.formState.isSubmitting || isDetailLoading || !isDirty || !isValid}
            />
          </>
        )
        }

      </div>

      {/* Modals */}
      <RightSideModal isOpen={modals.payee} onClose={() => toggleModal('payee')} title="Add Payee" buttonlabel="Save" formId="AddPayee" maxWidth={550} isLoading={payeeMethods.formState.isSubmitting} isDirty={payeeMethods.formState.isDirty} isSubmitDisabled={payeeMethods.formState.isSubmitting || !payeeMethods.formState.isDirty || !payeeMethods.formState.isValid}>
        <Card className="no-bg">
          <FormProvider {...payeeMethods}>
            <form id="AddPayee" onSubmit={payeeMethods.handleSubmit(onAddPayeeSubmit)}>
              <div className="row">
                <div className="col-md-12">
                  <InputField name="payeeName" label="Payee Name" required type="text" placeholder="Enter Payee Name" onlyAlphabets validation={{ maxLength: { value: 40, message: "Payee name cannot exceed 40 characters" } }} />
                </div>
                <div className="col-md-12">
                  <SelectField name="AccountTypeId" label="Account Type" required options={accountPayeeTypesOptions} placeholderText="Select Account Type" disabled={isLoading} />
                </div>
              </div>
            </form>
          </FormProvider>
        </Card>
      </RightSideModal>

      <RightSideModal isOpen={modals.viewAll} onClose={() => toggleModal('viewAll')} title="Dues & Transactions" isFooterHidden={true} maxWidth={480}>
        <Card className="p-0 no-bg">
          <DynamicTabs
            tabs={[
              { id: "dues", label: "Dues", onClick: handleTabChange, content: renderDuesContent() },
              { id: "transactions", label: "Transactions", onClick: handleTabChange, content: renderTransactionsContent() },
            ]}
            defaultActiveTab="dues"
          />
        </Card>
      </RightSideModal>
    </>
  );
};

export default EditPayable;